import { getLanguageCode, getLocaleCode } from 'locales';
import { IUploadMasterdata, UploadMasterdata, UploadMasterdataEnCSVEmpty, UploadMasterdataEnCSVEmptyEnhanced, UploadMasterdataEsCSVEmpty, UploadMasterdataEsCSVEmptyEnhanced, UploadMasterdataPtBrCSVEmpty, UploadMasterdataPtBrCSVEmptyEnhanced, UploadMasterdataRequiredFieldsEn, UploadMasterdataRequiredFieldsEs, UploadMasterdataRequiredFieldsPtBr } from './import-export-json.types';
import intl from 'react-intl-universal';
import { convertStringToBoolean } from 'utils/convert';
import { importMasterdata } from './import-export-json.service';
import { parseNumberByLocaleWithGroup } from 'locales/utils';

const getSupplierKeys = (isEnhanced?: boolean) => ({
  supplierNameKey: isEnhanced
    ? 'master-data.general-configuration.supplierCodeCustom'
    : 'master-data.general-configuration.supplierNameCustom',
  supplierCreateLoadCompositionKey: isEnhanced
    ? 'master-data.general-configuration.supplierCreateLoadCompositionEnhanced'
    : 'master-data.general-configuration.supplierCreateLoadComposition',
  supplierRoundingKey: isEnhanced
    ? 'master-data.general-configuration.supplierRoundingCustomEnhanced'
    : 'master-data.general-configuration.supplierRoundingCustom',
  supplierMinimumLotKey: isEnhanced
    ? 'master-data.general-configuration.supplierMinimumLotCustomEnhanced'
    : 'master-data.general-configuration.supplierMinimumLotCustom',
  supplierDeliveryBreakKey: isEnhanced
    ? 'master-data.general-configuration.supplierDeliveryBreakCustomEnhanced'
    : 'master-data.general-configuration.supplierDeliveryBreakCustom',
  supplierTransitTimeKey: isEnhanced
    ? 'master-data.general-configuration.supplierTransitTimeCustomEnhanced'
    : 'master-data.general-configuration.supplierTransitTimeCustom',
  supplierIncotermKey: isEnhanced
    ? 'master-data.general-configuration.supplierIncotermCustomEnhanced'
    : 'master-data.general-configuration.supplierIncotermCustom',
  responsibleUserEmailKey: isEnhanced
    ? 'master-data.general-configuration.responsibleUserEmailEnhanced'
    : 'master-data.general-configuration.responsibleUserEmail',
});

export const createPromises = async (
  fileData: UploadMasterdata[],
  treatSuccess: (data: any) => void,
  treatError: () => void,
  setProcessingPercentual: React.Dispatch<React.SetStateAction<number>>,
  isEnhanced?: boolean
) => {
  const responseSuccessRequestList = [];
  const responseErrorRequestList = [];
  const processingFileLength = fileData.length;
  let index = 1;
  const {
    supplierNameKey,
    supplierCreateLoadCompositionKey,
    supplierRoundingKey,
    supplierMinimumLotKey,
    supplierDeliveryBreakKey,
    supplierTransitTimeKey,
    supplierIncotermKey,
    responsibleUserEmailKey,
  } = getSupplierKeys(isEnhanced);

  for (const x of fileData) {
    const payloadData: IUploadMasterdata = {
      line: index,
      coordenacao: x[intl.get('master-data.general-configuration.coordination')],
      carteira: x[intl.get('commons.materialGroup')],
      familia: x[intl.get('commons.family')],
      familiaRotulada: x[intl.get('commons.labeledFamily')],
      descricaoMaterial: x[intl.get('master-data.configurations.imo-management.table.headers.description')],
      numeroMaterial: x[intl.get('master-data.general-configuration.materialNumber')],
      centro: x[intl.get('master-data.general-configuration.unit')],
      unidadeMedida: x[intl.get('master-data.general-configuration.unitMeasurement')],
      descricaoUnidadeMedida: x[intl.get('master-data.general-configuration.unitMeasurementDescription')],
      horizonteProgramacao: Number(x[intl.get('master-data.general-configuration.horizonSchedule')]),
      minEstoque: Number(x[intl.get('master-data.general-configuration.minimumStock')]),
      maxEstoque: Number(x[intl.get('master-data.general-configuration.maximumStock')]),
      porcentagemPontoReabastecimento: Number(x[intl.get('master-data.general-configuration.percentSupplyPoint')]),
      periodicity: x[intl.get('master-data.general-configuration.frequency')],
      autoAceiteComposicaoCarga: convertStringToBoolean(x[intl.get('master-data.general-configuration.autoAcceptLoadComposition')]),
      saneamentoDeVolumesPendentes: convertStringToBoolean(x[intl.get('master-data.general-configuration.sanitationPendingVolumes')]),
      replanejamentoDataEntrega: convertStringToBoolean(x[intl.get('master-data.general-configuration.replanningDeliveryDate')]),
      replanejamentoGradePedidos: Number(x[intl.get('master-data.general-configuration.ordersGridReplanning')]),
      active: convertStringToBoolean(x[intl.get('master-data.general.table.actions.editMaterial.table.header.active')]),
      production: convertStringToBoolean(x[intl.get('master-data.general.table.actions.editMaterial.table.header.production')]),
      responsibleUserEmail: x[intl.get(responsibleUserEmailKey)],
      //valor da Sugestão Externa fixo como false, posteriormente deve ser removida ao remover campo da tabela de FRC
      externalSuggestion: false,
      futureNecessity: convertStringToBoolean(x[intl.get('master-data.general-configuration.futureNeed')]),
      onlyTransferOrder: convertStringToBoolean(x[intl.get('master-data.general-configuration.onlyTransferOrder')]),
      autoPilot: convertStringToBoolean(x[intl.get('commons.autopilot')]),
      deflator: convertStringToBoolean(x[intl.get('commons.deflator')]),
      deflationPercentage: Number(x[intl.get('master-data.general-configuration.deflationPercentage')]),
      saneamentoDePedidos: convertStringToBoolean(x[intl.get('master-data.general-configuration.orderSanitation')]),
      quantidadeDiasParaSaneamentoPedidos: Number(x[intl.get('master-data.general-configuration.daysQuantityToOrdersSanitation')]),
      relevanciaOor: convertStringToBoolean(x[intl.get('master-data.general-configuration.relevanceOOR')]),
      quantidadeDeDiasAntecipadoParaGeracaoSugestao: Number(x[intl.get('master-data.general.auto-pilot.suggestionsFromD-')]),
      fornecedor_1_Nome: x[`${intl.get(supplierNameKey)} 1`],
      fornecedor_1_Arredondamento: Number(x[`${intl.get(supplierRoundingKey)} 1`]),
      fornecedor_1_LoteMinimo: Number(x[`${intl.get(supplierMinimumLotKey)} 1`]),
      fornecedor_1_QuebraDeEntrega: Number(x[`${intl.get(supplierDeliveryBreakKey)} 1`]),
      fornecedor_1_Incoterm: x[`${intl.get(supplierIncotermKey)} 1`],
      fornecedor_1_Sla: Number(x[`${intl.get('master-data.general-configuration.supplierSlaCustom')} 1`]),
      fornecedor_1_TransitTime: Number(x[`${intl.get(supplierTransitTimeKey)} 1`]),
      fornecedor_1_Create_Load_Composition: convertStringToBoolean(x[`${intl.get(supplierCreateLoadCompositionKey)} 1`]),
      fornecedor_2_Nome: x[`${intl.get(supplierNameKey)} 2`],
      fornecedor_2_Arredondamento: Number(x[`${intl.get(supplierRoundingKey)} 2`]),
      fornecedor_2_LoteMinimo: Number(x[`${intl.get(supplierMinimumLotKey)} 2`]),
      fornecedor_2_QuebraDeEntrega: Number(x[`${intl.get(supplierDeliveryBreakKey)} 2`]),
      fornecedor_2_Incoterm: x[`${intl.get(supplierIncotermKey)} 2`],
      fornecedor_2_Sla: Number(x[`${intl.get('master-data.general-configuration.supplierSlaCustom')} 2`]),
      fornecedor_2_TransitTime: Number(x[`${intl.get(supplierTransitTimeKey)} 2`]),
      fornecedor_2_Create_Load_Composition: convertStringToBoolean(x[`${intl.get(supplierCreateLoadCompositionKey)} 2`]),
      fornecedor_3_Nome: x[`${intl.get(supplierNameKey)} 3`],
      fornecedor_3_Arredondamento: Number(x[`${intl.get(supplierRoundingKey)} 3`]),
      fornecedor_3_LoteMinimo: Number(x[`${intl.get(supplierMinimumLotKey)} 3`]),
      fornecedor_3_QuebraDeEntrega: Number(x[`${intl.get(supplierDeliveryBreakKey)} 3`]),
      fornecedor_3_Incoterm: x[`${intl.get(supplierIncotermKey)} 3`],
      fornecedor_3_Sla: Number(x[`${intl.get('master-data.general-configuration.supplierSlaCustom')} 3`]),
      fornecedor_3_TransitTime: Number(x[`${intl.get(supplierTransitTimeKey)} 3`]),
      fornecedor_3_Create_Load_Composition: convertStringToBoolean(x[`${intl.get(supplierCreateLoadCompositionKey)} 3`]),
      fornecedor_4_Nome: x[`${intl.get(supplierNameKey)} 4`],
      fornecedor_4_Arredondamento: Number(x[`${intl.get(supplierRoundingKey)} 4`]),
      fornecedor_4_LoteMinimo: Number(x[`${intl.get(supplierMinimumLotKey)} 4`]),
      fornecedor_4_QuebraDeEntrega: Number(x[`${intl.get(supplierDeliveryBreakKey)} 4`]),
      fornecedor_4_Incoterm: x[`${intl.get(supplierIncotermKey)} 4`],
      fornecedor_4_Sla: Number(x[`${intl.get('master-data.general-configuration.supplierSlaCustom')} 4`]),
      fornecedor_4_TransitTime: Number(x[`${intl.get(supplierTransitTimeKey)} 4`]),
      fornecedor_4_Create_Load_Composition: convertStringToBoolean(x[`${intl.get(supplierCreateLoadCompositionKey)} 4`]),
      fornecedor_5_Nome: x[`${intl.get(supplierNameKey)} 5`],
      fornecedor_5_Arredondamento: Number(x[`${intl.get(supplierRoundingKey)} 5`]),
      fornecedor_5_LoteMinimo: Number(x[`${intl.get(supplierMinimumLotKey)} 5`]),
      fornecedor_5_QuebraDeEntrega: Number(x[`${intl.get(supplierDeliveryBreakKey)} 5`]),
      fornecedor_5_Incoterm: x[`${intl.get(supplierIncotermKey)} 5`],
      fornecedor_5_Sla: Number(x[`${intl.get('master-data.general-configuration.supplierSlaCustom')} 5`]),
      fornecedor_5_TransitTime: Number(x[`${intl.get(supplierTransitTimeKey)} 5`]),
      fornecedor_5_Create_Load_Composition: convertStringToBoolean(x[`${intl.get(supplierCreateLoadCompositionKey)} 5`]),
      fornecedor_6_Nome: x[`${intl.get(supplierNameKey)} 6`],
      fornecedor_6_Arredondamento: Number(x[`${intl.get(supplierRoundingKey)} 6`]),
      fornecedor_6_LoteMinimo: Number(x[`${intl.get(supplierMinimumLotKey)} 6`]),
      fornecedor_6_QuebraDeEntrega: Number(x[`${intl.get(supplierDeliveryBreakKey)} 6`]),
      fornecedor_6_Incoterm: x[`${intl.get(supplierIncotermKey)} 6`],
      fornecedor_6_Sla: Number(x[`${intl.get('master-data.general-configuration.supplierSlaCustom')} 6`]),
      fornecedor_6_TransitTime: Number(x[`${intl.get(supplierTransitTimeKey)} 6`]),
      fornecedor_6_Create_Load_Composition: convertStringToBoolean(x[`${intl.get(supplierCreateLoadCompositionKey)} 6`]),
      fornecedor_7_Nome: x[`${intl.get(supplierNameKey)} 7`],
      fornecedor_7_Arredondamento: Number(x[`${intl.get(supplierRoundingKey)} 7`]),
      fornecedor_7_LoteMinimo: Number(x[`${intl.get(supplierMinimumLotKey)} 7`]),
      fornecedor_7_QuebraDeEntrega: Number(x[`${intl.get(supplierDeliveryBreakKey)} 7`]),
      fornecedor_7_Incoterm: x[`${intl.get(supplierIncotermKey)} 7`],
      fornecedor_7_Sla: Number(x[`${intl.get('master-data.general-configuration.supplierSlaCustom')} 7`]),
      fornecedor_7_TransitTime: Number(x[`${intl.get(supplierTransitTimeKey)} 7`]),
      fornecedor_7_Create_Load_Composition: convertStringToBoolean(x[`${intl.get(supplierCreateLoadCompositionKey)} 7`]),
      fornecedor_8_Nome: x[`${intl.get(supplierNameKey)} 8`],
      fornecedor_8_Arredondamento: Number(x[`${intl.get(supplierRoundingKey)} 8`]),
      fornecedor_8_LoteMinimo: Number(x[`${intl.get(supplierMinimumLotKey)} 8`]),
      fornecedor_8_QuebraDeEntrega: Number(x[`${intl.get(supplierDeliveryBreakKey)} 8`]),
      fornecedor_8_Incoterm: x[`${intl.get(supplierIncotermKey)} 8`],
      fornecedor_8_Sla: Number(x[`${intl.get('master-data.general-configuration.supplierSlaCustom')} 8`]),
      fornecedor_8_TransitTime: Number(x[`${intl.get(supplierTransitTimeKey)} 8`]),
      fornecedor_8_Create_Load_Composition: convertStringToBoolean(x[`${intl.get(supplierCreateLoadCompositionKey)} 8`]),
      fornecedor_9_Nome: x[`${intl.get(supplierNameKey)} 9`],
      fornecedor_9_Arredondamento: Number(x[`${intl.get(supplierRoundingKey)} 9`]),
      fornecedor_9_LoteMinimo: Number(x[`${intl.get(supplierMinimumLotKey)} 9`]),
      fornecedor_9_QuebraDeEntrega: Number(x[`${intl.get(supplierDeliveryBreakKey)} 9`]),
      fornecedor_9_Incoterm: x[`${intl.get(supplierIncotermKey)} 9`],
      fornecedor_9_Sla: Number(x[`${intl.get('master-data.general-configuration.supplierSlaCustom')} 9`]),
      fornecedor_9_TransitTime: Number(x[`${intl.get(supplierTransitTimeKey)} 9`]),
      fornecedor_9_Create_Load_Composition: convertStringToBoolean(x[`${intl.get(supplierCreateLoadCompositionKey)} 9`]),
      fornecedor_10_Nome: x[`${intl.get(supplierNameKey)} 10`],
      fornecedor_10_Arredondamento: Number(x[`${intl.get(supplierRoundingKey)} 10`]),
      fornecedor_10_LoteMinimo: Number(x[`${intl.get(supplierMinimumLotKey)} 10`]),
      fornecedor_10_QuebraDeEntrega: Number(x[`${intl.get(supplierDeliveryBreakKey)} 10`]),
      fornecedor_10_Incoterm: x[`${intl.get(supplierIncotermKey)} 10`],
      fornecedor_10_Sla: Number(x[`${intl.get('master-data.general-configuration.supplierSlaCustom')} 10`]),
      fornecedor_10_TransitTime: Number(x[`${intl.get(supplierTransitTimeKey)} 10`]),
      fornecedor_10_Create_Load_Composition: convertStringToBoolean(x[`${intl.get(supplierCreateLoadCompositionKey)} 10`]),
      updateUploadInfo: index === 1
    };

    if (isEnhanced) {
      payloadData.reagendamentoDePedidosPorGrade = convertStringToBoolean(x[intl.get('master-data.general-configuration.orderReschedulingByGridCsv')]);
      payloadData.politicaDeEstoqueDinamica = convertStringToBoolean(x[intl.get('master-data.general-configuration.dynamicInventoryPolicyCsv')]);
      payloadData.quebrarPorCarro = convertStringToBoolean(x[intl.get('master-data.general-configuration.breakByCarCsv')]);
      payloadData.negociarComposicaoDeCarga = convertStringToBoolean(x[intl.get('master-data.general-configuration.negotiateLoadCompositionCsv')]);
      payloadData.liberarParaMixarCarga = convertStringToBoolean(x[intl.get('master-data.general-configuration.releaseToMixLoadCsv')]);
      payloadData.acionamentoDeTransporteCIF = convertStringToBoolean(x[intl.get('master-data.general-configuration.cifTransportActivationCsv')]);
      payloadData.localDeArmazenamento = convertStringToBoolean(x[intl.get('master-data.general-configuration.storageLocationCsv')]);
      payloadData.hourlyPlan = convertStringToBoolean(x[intl.get('master-data.general-configuration.hourlyPlanCsv')]);
      payloadData.gestaoDeEstoquesEmOver = convertStringToBoolean(x[intl.get('master-data.general-configuration.overStockManagementCsv')]);
      payloadData.corteDePedidosEmOverAPartirDeD = Number(x[intl.get('master-data.general-configuration.orderCutoffOverFromDCsv')]);

      for (let i = 1; i <= 10; i++) {
        const portDockKey = `${intl.get('master-data.general-configuration.supplierPortDockTransitTimeCsv')} ${i}`;
        const acceptsOrderKey = `${intl.get('master-data.general-configuration.supplierAcceptsOrderAutomaticallyCsv')} ${i}`;
        const acceptsVolumeKey = `${intl.get('master-data.general-configuration.supplierAcceptsVolumeAutomaticallyCsv')} ${i}`;
        const purchasingOrgKey = `${intl.get('master-data.general-configuration.supplierPurchasingOrganizationCsv')} ${i}`;
        const buyerGroupKey = `${intl.get('master-data.general-configuration.supplierBuyerGroupCsv')} ${i}`;
        const taxCodeKey = `${intl.get('master-data.general-configuration.supplierTaxCodeCsv')} ${i}`;

        (payloadData as unknown as Record<string, unknown>)[`fornecedor_${i}_TempoTransportePortoDoca`] = Number(x[portDockKey]);
        (payloadData as unknown as Record<string, unknown>)[`fornecedor_${i}_AceitaPedidoAutomaticamente`] = convertStringToBoolean(x[acceptsOrderKey]);
        (payloadData as unknown as Record<string, unknown>)[`fornecedor_${i}_AceitaVolumeAutomaticamente`] = convertStringToBoolean(x[acceptsVolumeKey]);
        (payloadData as unknown as Record<string, unknown>)[`fornecedor_${i}_OrganizacaoDeCompra`] = x[purchasingOrgKey] ?? '';
        (payloadData as unknown as Record<string, unknown>)[`fornecedor_${i}_GrupoDeCompradores`] = x[buyerGroupKey] ?? '';
        (payloadData as unknown as Record<string, unknown>)[`fornecedor_${i}_CodigoDeImposto`] = x[taxCodeKey] ?? '';
      }
    }

    try {
      const response = await importMasterdata(payloadData);
      responseSuccessRequestList.push(response.data);
    } catch (e) {
      responseErrorRequestList.push(e);
    }
    setProcessingPercentual(((index/processingFileLength)*100));
    index++;
  };

  if (responseSuccessRequestList.length > 0) {
    treatSuccess(responseSuccessRequestList);
  } else if (responseErrorRequestList.length > 0)
    treatError();
};

export const validateCsvEmpty = (isEnhanced?: boolean) => {
  switch (getLanguageCode()) {
    case "pt":
      return isEnhanced ? UploadMasterdataPtBrCSVEmptyEnhanced : UploadMasterdataPtBrCSVEmpty;
    case "es":
      return isEnhanced ? UploadMasterdataEsCSVEmptyEnhanced : UploadMasterdataEsCSVEmpty;
    case "en":
    default:
      return isEnhanced ? UploadMasterdataEnCSVEmptyEnhanced : UploadMasterdataEnCSVEmpty;
  }
};

export const validateRequiredFields = () => {
  switch (getLanguageCode()) {
    case "pt":
      return UploadMasterdataRequiredFieldsPtBr;
    case "es":
      return UploadMasterdataRequiredFieldsEs;
    case "en":
    default:
      return UploadMasterdataRequiredFieldsEn;
  }
};

export const validateDelimiter = () => {
  switch (getLocaleCode()) {
    case "en-US":
    case "es-MX":
    case "en-ZA":
    case "en-CA":
    case "en-GB":
      return ",";
    case "pt-BR":
    case "es-BO":
    case "es-ES":
    default:
      return ";";
  }
};

export const formatAndValidateNumber = (strNumber: string, validations: string[], field: string) => {
  try {
    const normalizedNumber = parseNumberByLocaleWithGroup(strNumber);

    if (normalizedNumber === null || normalizedNumber === undefined)
      throw new Error();

    const _number = Number(normalizedNumber);

    if (isNaN(_number))
      throw new Error()

    return _number;

  } catch (e) {
    validations.push(field);
    console.error(e);
    return null;
  }
}

interface IValidationsMasterdataResult {
  isValid: boolean,
  validationMessage: string[]
  data: UploadMasterdata[],
}

const getMissingFields = (data: string[]) => (`${data.join(', ')}.`);

const validateSupplierNumericFields = (
  x: UploadMasterdata,
  item: UploadMasterdata,
  lineValidations: string[],
  isEnhanced?: boolean
) => {
  for (let i = 1; i <= 10; i++) {
    const supplierRoundingIntl = `${intl.get(isEnhanced ? 'master-data.general-configuration.supplierRoundingCustomEnhanced' : 'master-data.general-configuration.supplierRoundingCustom')} ${i}`;
    const supplierMinimumLotIntl = `${intl.get(isEnhanced ? 'master-data.general-configuration.supplierMinimumLotCustomEnhanced' : 'master-data.general-configuration.supplierMinimumLotCustom')} ${i}`;
    const supplierDeliveryBreakIntl = `${intl.get(isEnhanced ? 'master-data.general-configuration.supplierDeliveryBreakCustomEnhanced' : 'master-data.general-configuration.supplierDeliveryBreakCustom')} ${i}`;
    const supplierSlaIntl = `${intl.get('master-data.general-configuration.supplierSlaCustom')} ${i}`;
    const supplierTransitTimeIntl = `${intl.get(isEnhanced ? 'master-data.general-configuration.supplierTransitTimeCustomEnhanced' : 'master-data.general-configuration.supplierTransitTimeCustom')} ${i}`;

    item[supplierRoundingIntl] = formatAndValidateNumber(x[supplierRoundingIntl], lineValidations, supplierRoundingIntl);
    item[supplierMinimumLotIntl] = formatAndValidateNumber(x[supplierMinimumLotIntl], lineValidations, supplierMinimumLotIntl);
    item[supplierDeliveryBreakIntl] = formatAndValidateNumber(x[supplierDeliveryBreakIntl], lineValidations, supplierDeliveryBreakIntl);
    item[supplierSlaIntl] = formatAndValidateNumber(x[supplierSlaIntl], lineValidations, supplierSlaIntl);
    item[supplierTransitTimeIntl] = formatAndValidateNumber(x[supplierTransitTimeIntl], lineValidations, supplierTransitTimeIntl);

    if (isEnhanced) {
      const portDockIntl = `${intl.get('master-data.general-configuration.supplierPortDockTransitTimeCsv')} ${i}`;
      item[portDockIntl] = formatAndValidateNumber(x[portDockIntl], lineValidations, portDockIntl);
    }
  }
};

export const formatAndValidateFile: (items: UploadMasterdata[], isEnhanced?: boolean) => IValidationsMasterdataResult = (items: UploadMasterdata[], isEnhanced?: boolean) => {
  const _result: IValidationsMasterdataResult = {
    data: [],
    isValid: true,
    validationMessage: []
  }

  let _items = items[0] as unknown as UploadMasterdata[];
  _items = _items?.filter(x => !Object.values(x).every(value => !value || value.trim() === ''));

  if (!_items?.length) {
    _result.isValid = false;
    _result.validationMessage.push(intl.get('master-data.general-configuration.invalidFile'));
    return _result;
  }

  const UploadMasterdataCSVEmpty = validateCsvEmpty(isEnhanced);
  const validateHeaders = Object.keys(UploadMasterdataCSVEmpty).every(x => Object.keys(_items[0]).includes(x));

  if (!validateHeaders) {
    _result.isValid = false;
    _result.validationMessage.push(intl.get('master-data.general-configuration.incorrectHeader'));
    return _result;
  }

  let line = 2;

  for (const x of _items) {
    const requiredFields = validateRequiredFields();
    const dataMissing = requiredFields?.filter((field) => {
      const value = x[field];
      return !value || value.trim() === ''
    });

    if (dataMissing.length !== 0) {
      _result.isValid = false;
      _result.validationMessage.push(`${intl.get('master-data.general-configuration.missingRequiredFields')} ${line}: ${getMissingFields(dataMissing)}`)
      line++;
      continue;
    }

    const lineValidations: string[] = [];
    try {

      const item: UploadMasterdata = x as unknown as UploadMasterdata;
      const horizonScheduleIntl = intl.get('master-data.general-configuration.horizonSchedule');
      const minStockIntl = intl.get('master-data.general-configuration.minimumStock');
      const maxStockIntl = intl.get('master-data.general-configuration.maximumStock');
      const replenishmentPercentageIntl = intl.get('master-data.general-configuration.percentSupplyPoint');
      const reschedulingGradeIntl = intl.get('master-data.general-configuration.ordersGridReplanning');
      const deflationPercentageIntl = intl.get('master-data.general-configuration.deflationPercentage');
      const sanitationDaysIntl = intl.get('master-data.general-configuration.daysQuantityToOrdersSanitation');
      const suggestionGenerationDaysIntl = intl.get('master-data.general.auto-pilot.suggestionsFromD-');

      const _horizonSchedule = formatAndValidateNumber(x[horizonScheduleIntl], lineValidations, horizonScheduleIntl);
      const _minStock = formatAndValidateNumber(x[minStockIntl], lineValidations, minStockIntl);
      const _maxStock = formatAndValidateNumber(x[maxStockIntl], lineValidations, maxStockIntl);
      const _replenishmentPercentage = formatAndValidateNumber(x[replenishmentPercentageIntl], lineValidations, replenishmentPercentageIntl);
      const _reschedulingGrade = formatAndValidateNumber(x[reschedulingGradeIntl], lineValidations, reschedulingGradeIntl);
      const _deflationPercentageIntl = formatAndValidateNumber(x[deflationPercentageIntl], lineValidations, deflationPercentageIntl);
      const _sanitationDays = formatAndValidateNumber(x[sanitationDaysIntl], lineValidations, sanitationDaysIntl);
      const _suggestionGenerationDays = formatAndValidateNumber(x[suggestionGenerationDaysIntl], lineValidations, suggestionGenerationDaysIntl);

      item[horizonScheduleIntl] = _horizonSchedule;
      item[minStockIntl] = _minStock;
      item[maxStockIntl] = _maxStock;
      item[replenishmentPercentageIntl] = _replenishmentPercentage;
      item[reschedulingGradeIntl] = _reschedulingGrade;
      item[deflationPercentageIntl] = _deflationPercentageIntl;
      item[sanitationDaysIntl] = _sanitationDays;
      item[suggestionGenerationDaysIntl] = _suggestionGenerationDays;

      if (isEnhanced) {
        const orderCutoffIntl = intl.get('master-data.general-configuration.orderCutoffOverFromDCsv');
        const _orderCutoff = formatAndValidateNumber(x[orderCutoffIntl], lineValidations, orderCutoffIntl);
        item[orderCutoffIntl] = _orderCutoff;
      }

      validateSupplierNumericFields(x, item, lineValidations, isEnhanced);

      if (lineValidations.length) {
        _result.isValid = false;
        _result.validationMessage.push(`${intl.get('commons.line')} ${line} - ${intl.get('master-data.general-configuration.fieldErrors')} ${lineValidations.join(', ')}`)
        line++;
        continue;
      }

      _result.data.push(item)
    }
    catch (e) {
      _result.isValid = false;
      _result.validationMessage.push(`${intl.get('master-data.general-configuration.errorLine')} ${line}`)
      console.error(e);
    }

    line++;
  }

  return _result;
}

export const getMessageForImport = (importedWithSuccess: boolean, contextModal: string) => importedWithSuccess ?
  intl.get(`exportImportModal.${contextModal}.importSuccess`) : intl.get(`exportImportModal.${contextModal}.notSucceedingImport`);

export const getColorBorder = (type: number) => {
  if (type === 0) {
    return 'red';
  } else if (type === 1) {
    return 'yellow';
  } else {
    return 'green';
  }
};